import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { select, Store } from '@ngrx/store';

import { FeatureCollection } from 'geojson';
import { cloneDeep, compact, each, find, has, includes, isNil, isNull, isUndefined, map, values } from 'lodash-es';
import { BehaviorSubject, combineLatest, Observable, tap } from 'rxjs';
import { concatMap, filter } from 'rxjs/operators';

import { MainTabBasePathEnum } from '../../../../../../enums/main-tabs';
import { ProfileSearchEnum } from '../../../../../../enums/profile-search';
import {
  FarmSizeIdentifierEnum,
  WalkingFarmLeadStatusEnum,
  WalkingFarmViewTabEnum
} from '../../../../../../enums/walking-farm';
import * as fromMonetizationActions from '../../../../../../store/actions/monetization.actions';
import * as fromWalkingFarmActions from '../../../../../../store/actions/walking-farm.actions';
import * as fromMapSelectors from '../../../../../../store/selectors/map.selectors';
import * as fromMonetizationSelectors from '../../../../../../store/selectors/monetization.selectors';
import * as fromOrganizationSelectors from '../../../../../../store/selectors/organization.selectors';
import * as fromWalkingFarmSelectors from '../../../../../../store/selectors/walking-farm.selectors';
import { WalkingFarmInAppPurchaseDetails, WalkingFarmProperty } from '../../../../../../typings/walking-farm';
import { GenericComponent } from '../../../../../components/generic/generic.component';
import { MapInfoWindowComponent } from '../map-info-window/map-info-window.component';

interface MarkerData {
  options: google.maps.MarkerOptions;
  position: google.maps.LatLngLiteral;
  propertyId: string | number;
  propertyStatus: string | undefined;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-walking-farm-view-map',
  standalone: false,
  styleUrls: ['./map.component.scss'],
  templateUrl: './map.component.html'
})
export class MapComponent extends GenericComponent implements OnChanges {
  private activatedRoute = inject(ActivatedRoute);
  private store = inject(Store);

  @ViewChild(MapInfoWindowComponent) mapInfoWindowComponent?: MapInfoWindowComponent;
  @Input() walkingFarmId: string | null = null;
  walkingFarmId$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  mapInstance: google.maps.Map | null = null;
  selectedMapTypeId: google.maps.MapTypeId | null = null;

  mapOptions$: Observable<google.maps.MapOptions>;
  walkingFarmBounds: google.maps.LatLngBounds | null = null;
  markerData$: BehaviorSubject<MarkerData[]> = new BehaviorSubject<MarkerData[]>([]);
  boundariesVisible$: Observable<boolean>;
  boundariesEnabled$: Observable<boolean>;
  phoneAndEmailPurchaseEnabled$: Observable<boolean>;

  boundariesData: FeatureCollection | null = null;
  boundariesEnabled = false;
  boundariesVisible = false;

  selectedTab = WalkingFarmViewTabEnum.Map;

  readonly walkingFarmViewTabs = WalkingFarmViewTabEnum;

  selectedProperty: WalkingFarmProperty | null = null;
  isInfoWindowOpened = false;
  isDrawMode = false;

  properties: WalkingFarmProperty[] = [];
  phoneAndEmailInAppPurchaseDetails?: WalkingFarmInAppPurchaseDetails;

  defaultBackHref: string | undefined;

  constructor() {
    super();
    this.defaultBackHref = this.activatedRoute.snapshot.data['defaultBackHref'];

    this.mapOptions$ = this.store.pipe(select(fromMapSelectors.selectOptions));
    this.store.pipe(select(fromMapSelectors.selectCurrentType)).subscribe((selectedMapTypeId) => {
      this.selectedMapTypeId = selectedMapTypeId;
      this.updateMapType();
    });

    this.walkingFarmId$
      .pipe(
        filter((id) => !isNil(id)),
        concatMap((id) => this.store.pipe(select(fromWalkingFarmSelectors.selectWalkingFarmInAppPurchases(id)))),
        filter((inAppPurchases) => !isUndefined(inAppPurchases))
      )
      .subscribe((inAppPurchases) => {
        each(inAppPurchases, (value, key) => {
          let continueLoop = true;
          if (includes(values<string>(FarmSizeIdentifierEnum), key)) {
            this.phoneAndEmailInAppPurchaseDetails = value;
            continueLoop = false;
          }
          return continueLoop;
        });
      });

    combineLatest([
      this.walkingFarmId$.pipe(
        filter((id) => !isNil(id)),
        concatMap((id) => this.store.pipe(select(fromWalkingFarmSelectors.selectWalkingFarmProperties(id)))),
        filter((walkingFarm) => !isUndefined(walkingFarm))
      ),
      this.store.pipe(select(fromMapSelectors.selectMarkerOptions)),
      this.store.pipe(select(fromOrganizationSelectors.selectFarmAlertsEnabled))
    ]).subscribe(([walkingFarmProperties, markerOptions, organizationFarmAlertsEnabled]) => {
      this.walkingFarmBounds = new google.maps.LatLngBounds();
      this.properties = cloneDeep(walkingFarmProperties);
      this.markerData$.next(
        compact(
          map(walkingFarmProperties, (property) => {
            let result: MarkerData | null = null;
            if (!isNull(property.A042_Longitude) && !isNull(property.A041_Latitude)) {
              let markerImageUrl = '/assets/img/pin-';
              markerImageUrl += property.A040_OwnerOccupied === 'N' ? 'unoccupied-' : 'occupied-';

              switch (property.Status?.toLowerCase()) {
                case WalkingFarmLeadStatusEnum.Lead:
                  markerImageUrl += 'lead';
                  break;
                case WalkingFarmLeadStatusEnum.NotContacted:
                  markerImageUrl += 'not-contacted';
                  break;
                case WalkingFarmLeadStatusEnum.NotHome:
                  markerImageUrl += 'not-home';
                  break;
                case WalkingFarmLeadStatusEnum.NotInterested:
                  markerImageUrl += 'not-interested';
                  break;
                case WalkingFarmLeadStatusEnum.OnMarket:
                  markerImageUrl += 'on-market';
                  break;
                case WalkingFarmLeadStatusEnum.Prospect:
                  markerImageUrl += 'prospect';
                  break;
                default:
                  markerImageUrl += 'not-contacted';
                  break;
              }

              if (organizationFarmAlertsEnabled && property.unreadFarmAlerts) {
                markerImageUrl += '-alert';
              }
              markerImageUrl += '.png';

              result = {
                options: {
                  ...markerOptions,
                  clickable: true,
                  icon: {
                    scaledSize: new google.maps.Size(26, 44),
                    url: markerImageUrl
                  },
                  optimized: false,
                  propertyId: property.A000_PropertyNumber
                } as unknown as google.maps.MarkerOptions,
                position: {
                  lat: parseFloat(`${property.A041_Latitude}`),
                  lng: parseFloat(`${property.A042_Longitude}`)
                },
                propertyId: property.A000_PropertyNumber,
                propertyStatus: property.Status
              };
              this.walkingFarmBounds?.extend(result.position);
            }
            if (this.selectedProperty && this.selectedProperty.A000_PropertyNumber === property.A000_PropertyNumber) {
              this.selectedProperty = property;
            }
            return result;
          })
        )
      );
      if (!this.isInfoWindowOpened && !this.isDrawMode) {
        this.centerOnBounds();
      }
    });
    this.boundariesVisible$ = this.store.pipe(
      select(fromWalkingFarmSelectors.selectWalkingFarmBoundariesVisible),
      tap((visible) => {
        this.boundariesVisible = visible;
        this.updateBoundariesStyle();
      })
    );
    this.boundariesEnabled$ = this.walkingFarmId$.pipe(
      filter((id) => !isNil(id)),
      concatMap((id) => this.store.pipe(select(fromWalkingFarmSelectors.selectWalkingFarmBoundariesEnabled(id)))),
      filter((enabled) => !isUndefined(enabled)),
      tap((enabled) => {
        this.boundariesEnabled = enabled;
        this.updateBoundariesStyle();
      })
    );
    this.phoneAndEmailPurchaseEnabled$ = this.store.pipe(
      select(fromMonetizationSelectors.selectPhoneAndEmailPurchaseEnabled)
    );
    this.walkingFarmId$
      .pipe(
        filter((id) => !isNil(id)),
        concatMap((id) => this.store.pipe(select(fromWalkingFarmSelectors.selectWalkingFarmBoundariesData(id)))),
        filter((data) => !isUndefined(data))
      )
      .subscribe((data) => {
        this.boundariesData = data;
        this.updateBoundariesData();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (has(changes, 'walkingFarmId')) {
      this.walkingFarmId$.next(this.walkingFarmId);
    }
  }

  centerOnBounds() {
    if (this.mapInstance && this.walkingFarmBounds && !this.walkingFarmBounds.isEmpty()) {
      this.mapInstance.fitBounds(this.walkingFarmBounds);
    }
  }

  updateBoundariesData() {
    if (this.mapInstance && this.boundariesData) {
      this.mapInstance.data.forEach((feature) => {
        this.mapInstance?.data.remove(feature);
      });
      this.mapInstance.data.addGeoJson(this.boundariesData, { idPropertyName: 'boundariesData' });
    }
  }

  updateBoundariesStyle() {
    if (this.mapInstance) {
      this.mapInstance.data.setStyle({
        clickable: false,
        fillOpacity: 0,
        strokeColor: '#ff0000',
        strokeOpacity: 0.8,
        strokeWeight: 2.5,
        visible: this.boundariesEnabled && this.boundariesVisible
      });
    }
  }

  mapInitialized(mapInstance: google.maps.Map) {
    this.mapInstance = mapInstance;
    this.centerOnBounds();
    this.updateBoundariesData();
    this.updateBoundariesStyle();
    this.updateMapType();
  }

  updateMapType() {
    if (!isNull(this.selectedMapTypeId)) {
      this.mapInstance?.setMapTypeId(this.selectedMapTypeId);
    }
  }

  toggleParcelVisibility() {
    this.store.dispatch(fromWalkingFarmActions.toggleBoundariesVisible());
  }

  openInfoWindow(markerData: MarkerData) {
    this.selectedProperty = find(this.properties, { A000_PropertyNumber: markerData.propertyId }) ?? null;
    this.mapInfoWindowComponent?.open();
    this.isInfoWindowOpened = true;
  }

  closeInfoWindow() {
    this.isInfoWindowOpened = false;
  }

  async openPurchasePhoneAndEmailModal() {
    if (this.walkingFarmId) {
      this.store.dispatch(
        fromMonetizationActions.openPurchasePhoneAndEmailModal({
          payload: {
            farmId: this.walkingFarmId,
            searchType:
              this.defaultBackHref === MainTabBasePathEnum.PropertySearch
                ? ProfileSearchEnum.PropertySearchWalkingFarm
                : ProfileSearchEnum.WalkingFarm
          }
        })
      );
    }
  }
}
